import type { DateCellData } from './types';

import {
  isCustomCellData,
  getCustomCellTemplate,
  isKindOfCustomCell,
} from '../utils';

import { kind } from './types';

import { DateEnGB, DateEnUS, DateEnCA, DEFAULT_LANG } from './constants';

const isFutureDate = (date: Date, now: Date = new Date()) =>
  date.getTime() > now.getTime();

const baseDateBoundary = new Date(1900, 0, 1);

const isAncientDate = (date: Date) =>
  date.getTime() < baseDateBoundary.getTime();

const isDate = (date: Date | null): date is Date =>
  date !== null && !isNaN(date.valueOf());

export const getFomattedDateString = (
  date: Date | null,
  lang: DateCellData['lang'] = DEFAULT_LANG
) => {
  if (date === null) return '';
  if (lang === 'en-US') return DateEnUS.format(date);
  if (lang === 'en-CA') return DateEnCA.format(date);
  return DateEnGB.format(date);
};

export const getDefaultCellData = ({
  value = null,
  error = null,
  lang = DEFAULT_LANG,
}: Partial<DateCellData> = {}): DateCellData => ({
  kind,
  lang,
  value,
  error,
});

const isCellValue = (value: unknown): value is DateCellData['value'] => {
  if (value instanceof Date && !isNaN(value.valueOf())) return true;
  if (value === null) return true;
  return false;
};

const isCellData = (data: unknown): data is DateCellData => {
  if (!isCustomCellData(data)) return false;
  return data.kind === kind;
};

type Args = Pick<Partial<DateCellData>, 'error' | 'lang'> & {
  value?: unknown;
};

/**
 * ## getCellTemplate
 *
 * @param value The value of the DateCell
 * @returns A custom DateCell object and its data
 */
export const getCellTemplate = ({ value = null, error, lang }: Args = {}) => {
  if (isCellValue(value)) {
    const cellData = getDefaultCellData({ value, error, lang });
    const copyData = isDate(value) ? getFomattedDateString(value, lang) : '';

    return getCustomCellTemplate({ cellData, copyData });
  }
  return getCustomCellTemplate({ cellData: getDefaultCellData({ lang }) });
};

export const isCell = (
  cell: unknown
): cell is ReturnType<typeof getCellTemplate> => {
  if (typeof cell !== 'object') return false;
  if (cell === null) return false;
  if (isKindOfCustomCell(cell)) {
    return isCellData(cell.data);
  }
  return false;
};

export const getCellData = (data?: unknown) => {
  if (isCellData(data) && isCellValue(data.value)) {
    return data;
  }

  return getDefaultCellData();
};

export const validateDate = (date: Date | null): string | null => {
  if (!isDate(date)) {
    return 'Date not valid';
  }

  if (isFutureDate(date)) {
    return `Date can't be in the future`;
  }

  if (isAncientDate(date)) {
    return `Date can't be before 1900`;
  }

  return null;
};

const convertFormat = (
  value: string,
  lang: DateCellData['lang']
): Date | null => {
  const parts = value.split('/');

  if (parts.length !== 3) {
    return null;
  }

  const [a, b, c] = parts;

  const formatMap = {
    'en-GB': [
      `${b}/${a}/${c}`, // DD/MM/YYYY (preferred for en-GB)
      `${a}/${b}/${c}`, // MM/DD/YYYY
      `${c}/${a}/${b}`, // YYYY/MM/DD
    ],
    'en-US': [
      `${a}/${b}/${c}`, // MM/DD/YYYY (preferred for en-US)
      `${b}/${a}/${c}`, // DD/MM/YYYY
      `${c}/${a}/${b}`, // YYYY/MM/DD
    ],
    'en-CA': [
      `${a}/${b}/${c}`, // YYYY/MM/DD (preferred for en-CA)
      `20${a}/${b}/${c}`, // YYYY/MM/DD
    ],
  };

  const formats = formatMap[lang] || formatMap['en-US'];

  const date = formats
    .map((format) => new Date(format))
    .find((date) => isDate(date));

  return date || null;
};

export const validatePastedValue = (
  pastedValue: string,
  cell: DateCellData
): DateCellData => {
  const { lang } = cell;

  const santiasedValue = pastedValue.replace(/\s/g, '').replace(/\.|-/g, '/');

  const date = convertFormat(santiasedValue, lang);

  const error = validateDate(date);

  if (error !== null) {
    return {
      ...cell,
      value: null,
      error,
    };
  }

  return {
    ...cell,
    value: date,
    error: null,
  };
};
